import React, { useState, useEffect, useMemo } from "react";
import Sidebar from "../../component/Sidebar";
import Topbar from "../../component/Topbar";

import {
 FiPlus,
 FiEye,
 FiSearch,
 FiUser,
 FiDollarSign,
 FiChevronsUp,
 FiChevronsDown,
 FiList,
 FiChevronLeft,
 FiChevronRight,
 FiFileText,
 FiShoppingBag, // Icon for Purchase Order
 FiTruck, // Icon for GRN
 FiCreditCard, // Icon for Bill/Payment
} from "react-icons/fi";

// --- NEW MOCK DATA / UTILS ---
// Data represents Purchase Orders (POs) and their related GRN/Bill status
const MOCK_POS = [
 { id: "PO-1001", supplierName: "Raw Material Co", poDate: "2025-11-01", totalAmount: 5500.0, 
    grnStatus: "Complete", billStatus: "Paid", 
    grnId: "GRN-045", billId: "BILL-006" },

 { id: "PO-1002", supplierName: "Office Supplies Inc", poDate: "2025-11-15", totalAmount: 850.5, 
    grnStatus: "Pending", billStatus: "N/A", 
    grnId: null, billId: null },

 { id: "PO-1003", supplierName: "Logistics Partner", poDate: "2025-12-01", totalAmount: 1200.0, 
    grnStatus: "Complete", billStatus: "Pending", 
    grnId: "GRN-046", billId: "BILL-011" },

 { id: "PO-1004", supplierName: "Marketing Agency", poDate: "2025-12-05", totalAmount: 4500.0, 
    grnStatus: "N/A (Service)", billStatus: "Draft", 
    grnId: null, billId: "BILL-012" },

 { id: "PO-1005", supplierName: "IT Services Ltd", poDate: "2025-12-08", totalAmount: 2200.0, 
    grnStatus: "Complete", billStatus: "Overdue", 
    grnId: "GRN-047", billId: "BILL-013" },

 { id: "PO-1006", supplierName: "Raw Material Co", poDate: "2025-12-10", totalAmount: 7800.0, 
    grnStatus: "Complete", billStatus: "Pending", 
    grnId: "GRN-048", billId: "BILL-014" },
];

const formatCurrency = (amount) => {
 return Number(amount).toLocaleString("en-IN", {
  style: "currency",
  currency: "INR",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
 });
};

// Reused Pagination component (internal) - Unchanged
const Pagination = ({ totalItems, itemsPerPage, currentPage, onPageChange }) => {
 const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));
 if (totalItems === 0) return null;

 let start = Math.max(1, currentPage - 2);
 let end = Math.min(totalPages, currentPage + 2);
 if (currentPage <= 3) {
  start = 1;
  end = Math.min(5, totalPages);
 } else if (currentPage + 2 >= totalPages) {
  end = totalPages;
  start = Math.max(1, totalPages - 4);
 }

 const pages = [];
 for (let i = start; i <= end; i++) pages.push(i);

 return (
  <nav aria-label="pagination">
   <ul className="pagination mb-0">
    <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
     <button className="page-link" onClick={() => onPageChange(1)} disabled={currentPage === 1}>
      First
     </button>
    </li>
    <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
     <button className="page-link" onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 1}>
      <FiChevronLeft /> Prev
     </button>
    </li>

    {pages.map((p) => (
     <li key={p} className={`page-item ${currentPage === p ? "active" : ""}`}>
      <button className="page-link" onClick={() => onPageChange(p)}>
       {p}
      </button>
     </li>
    ))}

    <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
     <button className="page-link" onClick={() => onPageChange(currentPage + 1)} disabled={currentPage === totalPages}>
      Next <FiChevronRight />
     </button>
    </li>
    <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
     <button className="page-link" onClick={() => onPageChange(totalPages)} disabled={currentPage === totalPages}>
      Last
     </button>
    </li>
   </ul>
  </nav>
 );
};

// --- MAIN COMPONENT ---
function PurchaseOrderTracker() {
 // Renamed state to purchaseOrders
 const [purchaseOrders, setPurchaseOrders] = useState([]);
 const [loading, setLoading] = useState(true);

 // Filters & UI state
 const [searchTerm, setSearchTerm] = useState("");
 // Status filter now targets the overall PO lifecycle stage
 const [poStatusFilter, setPoStatusFilter] = useState("All"); 
 const [dateRange, setDateRange] = useState({ start: "", end: "" });
 // Sort by poDate
 const [sortBy, setSortBy] = useState({ key: "poDate", direction: "desc" });

 // Pagination
 const [currentPage, setCurrentPage] = useState(1);
 const [itemsPerPage, setItemsPerPage] = useState(10);

 // Fetch mock data
 useEffect(() => {
  setLoading(true);
  const t = setTimeout(() => {
   setPurchaseOrders(MOCK_POS);
   setLoading(false);
  }, 500);
  return () => clearTimeout(t);
 }, []);


 // Helper to determine PO overall status for filtering (e.g., "PO raised", "Awaiting GRN", "Awaiting Bill", "Complete")
 const getOverallStatus = (po) => {
        if (po.billStatus === "Paid") return "Closed";
        if (po.grnStatus === "Complete" && po.billStatus === "Pending") return "Awaiting Payment";
        if (po.grnStatus === "Pending" || po.grnStatus === "N/A (Service)") return "Awaiting Receipt";
        if (po.grnStatus === "Complete" && po.billStatus === "Overdue") return "Payment Overdue";
        return "Open"; // Default status
  };


 // Memoized processed list (filter + sort)
 const processedPOs = useMemo(() => {
  let list = [...purchaseOrders];

  // filter by search
  if (searchTerm.trim() !== "") {
   const s = searchTerm.trim().toLowerCase();
   list = list.filter(
    (i) =>
     i.id.toLowerCase().includes(s) ||
     i.supplierName.toLowerCase().includes(s) || 
     String(i.totalAmount).toLowerCase().includes(s)
   );
  }

  // status filter (using the newly defined overall status)
  if (poStatusFilter !== "All") {
        list = list.filter((i) => getOverallStatus(i) === poStatusFilter);
    }

  // date range filter (poDate)
  if (dateRange.start) list = list.filter((i) => i.poDate >= dateRange.start);
  if (dateRange.end) list = list.filter((i) => i.poDate <= dateRange.end);

  // sort
  list.sort((a, b) => {
   const aVal = a[sortBy.key];
   const bVal = b[sortBy.key];

   let cmp = 0;
   if (sortBy.key === "totalAmount") cmp = Number(aVal) - Number(bVal);
   else if (sortBy.key === "supplierName") cmp = String(aVal).localeCompare(String(bVal));
   else cmp = String(aVal).localeCompare(String(bVal));

   return sortBy.direction === "asc" ? cmp : -cmp;
  });

  return list;
 }, [purchaseOrders, searchTerm, poStatusFilter, dateRange, sortBy]);

 // Reset page to 1 when filters change 
 useEffect(() => {
  setCurrentPage(1);
 }, [searchTerm, poStatusFilter, dateRange, sortBy, itemsPerPage]);

 // pagination calculations
 const totalItems = processedPOs.length;
 const startIndex = (currentPage - 1) * itemsPerPage;
 const endIndex = Math.min(startIndex + itemsPerPage, totalItems);
 const paginated = processedPOs.slice(startIndex, endIndex);

 // KPIs
 const kpis = useMemo(() => {
  const total = purchaseOrders.length;
  // Total POs closed (paid)
  const totalClosed = purchaseOrders.filter((i) => i.billStatus === "Paid").length;
  // Total PO value currently awaiting payment
  const awaitingPaymentValue = purchaseOrders.filter((i) => i.billStatus === "Pending" || i.billStatus === "Overdue").reduce((s, i) => s + i.totalAmount, 0);

  return {
   total,
   totalClosed,
   awaitingPaymentValue: formatCurrency(awaitingPaymentValue),
  };
 }, [purchaseOrders]);

 // Sorting handler - Unchanged
 const handleSort = (key) => {
  setSortBy((prev) => {
   if (prev.key === key) return { key, direction: prev.direction === "asc" ? "desc" : "asc" };
   return { key, direction: "desc" };
  });
 };

 const SortIcon = ({ columnKey }) => {
  if (sortBy.key !== columnKey) return null;
  return sortBy.direction === "asc" ? <FiChevronsUp size={14} className="ms-1 text-muted" /> : <FiChevronsDown size={14} className="ms-1 text-muted" />;
 };

 const getGrnStatusBadge = (status) => {
  let cls = "bg-secondary";
  if (status === "Complete") cls = "bg-success";
  else if (status === "Pending") cls = "bg-warning text-dark";
  else if (status === "N/A (Service)") cls = "bg-info text-dark";
  return <span className={`badge ${cls} status-badge`}>{status}</span>;
 };

 const getBillStatusBadge = (status) => {
    let cls = "bg-secondary";
    if (status === "Paid") cls = "bg-success";
    else if (status === "Overdue") cls = "bg-danger";
    else if (status === "Pending") cls = "bg-warning text-dark";
    else if (status === "Draft") cls = "bg-info text-dark";
    else if (status === "N/A") cls = "bg-secondary text-light"; // Bill not yet received/created
    return <span className={`badge ${cls} status-badge`}>{status}</span>;
 };


 // actions (stubs)
 const handleViewPO = (id) => {
  alert(`View Purchase Order ${id}`);
 };
 const handleCreateNewPO = () => {
  alert("Go to create new Purchase Order page");
 };
  const handleCreateGRN = (poId) => {
    alert(`Initiate GRN process for PO ${poId}`);
  };
  const handleCreateBill = (poId) => {
    alert(`Enter Bill details for PO ${poId}`);
  };

 return (
  <>
   <Topbar />
   <Sidebar />

   <main className="main-wrapper">
    <div className="page-inner">
     {/* Header */}
     <div className="d-flex justify-content-between align-items-center mb-4">
      <div>
       <h2 className="mb-2 d-flex align-items-center">
        <FiShoppingBag className="me-2 text-primary" />Purchase Order Tracker (P2P)
       </h2>
       <small className="text-muted">Track Purchase Order status through Goods Receipt and Billing (P2P Cycle)</small>
      </div>

      <div className="d-flex align-items-center" style={{ gap: 12 }}>
       <button className="btn btn-outline-secondary d-none d-sm-inline-flex align-items-center" onClick={() => { setSearchTerm(""); setPoStatusFilter("All"); setDateRange({ start: "", end: "" }); }}>
        Reset Filters
       </button>
       <button className="btn btn-primary btn-pill d-flex align-items-center" onClick={handleCreateNewPO}>
        <FiPlus className="me-2" /> Create PO
       </button>
      </div>
     </div>

 

     {/* Main Card */}
     <div className="card card-ghost p-0">
      {/* Filter bar */}
      <div className="p-3 border-bottom">
       <div className="d-flex align-items-center justify-content-between" >
        <div className="d-flex align-items-center" style={{ gap: 10 }}>
         <small className="text-muted me-2">Show</small>
         <select className="form-select form-select-sm" style={{ width: 80 }} value={itemsPerPage} onChange={(e) => setItemsPerPage(Number(e.target.value))}>
          <option value={5}>5</option>
          <option value={10}>10</option>
          <option value={20}>20</option>
          <option value={MOCK_POS.length}>All</option>
         </select>

                    <small className="text-muted ms-3 me-2">Filter by Stage:</small>
                    <select className="form-select form-select-sm" style={{ width: 180 }} value={poStatusFilter} onChange={(e) => setPoStatusFilter(e.target.value)}>
                        <option value="All">All Stages</option>
                        <option value="Open">PO Raised / Open</option>
                        <option value="Awaiting Receipt">Awaiting GRN</option>
                        <option value="Awaiting Payment">Awaiting Bill/Payment</option>
                        <option value="Payment Overdue">Payment Overdue</option>
                        <option value="Closed">Closed / Paid</option>
                    </select>

        </div>

        <div className="input-group search-input" style={{ width: 300 }}>
         <input type="text" className="form-control form-control-sm" placeholder="Search PO ID, supplier or amount..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
         <span className="input-group-text"><FiSearch /></span>
        </div>
       </div>
      </div>

      {/* Table */}
      <div className=" p-2">
       {loading ? (
        <div className="p-5 text-center">
         <div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div>
         <div className="mt-2 text-muted">Loading Purchase Orders...</div>
        </div>
       ) : (
        <div className="table-responsive">
         <table className="table mb-0 ">
          <thead className=" ">
           <tr>
            <th style={{ width: "12%" }} onClick={() => handleSort("id")} className="cursor-pointer">PO ID <SortIcon columnKey="id" /></th>
            <th style={{ width: "22%" }} onClick={() => handleSort("supplierName")} className="cursor-pointer">Supplier <SortIcon columnKey="supplierName" /></th>
            <th style={{ width: "12%" }} onClick={() => handleSort("poDate")} className="cursor-pointer">PO Date <SortIcon columnKey="poDate" /></th>
            <th style={{ width: "12%", textAlign: "right" }} onClick={() => handleSort("totalAmount")} className="cursor-pointer">PO Value <SortIcon columnKey="totalAmount" /></th>
            <th style={{ width: "12%" }}>GRN Status</th>
                        <th style={{ width: "12%" }}>Bill Status</th>
            <th style={{ width: "18%" }}>Actions</th>
           </tr>
          </thead>

          <tbody>
           {paginated.length === 0 ? (
            <tr>
             <td colSpan="7" className="text-center p-5 text-muted">
              <div style={{ fontSize: 28, marginBottom: 8 }}>🔍</div>
              No Purchase Orders found matching the criteria.
             </td>
            </tr>
           ) : (
            paginated.map((po) => (
             <tr key={po.id}>
              <td><strong>{po.id}</strong></td>
              <td>
               <div className="d-flex align-items-center">
                <div style={{ width: 36, height: 36, borderRadius: 8, background: "#f1f5f9", display: "flex", alignItems: "center", justifyContent: "center", marginRight: 10 }}>
                 <FiUser className="text-muted" />
                </div>
                <div>
                 <div className="fw-bold">{po.supplierName}</div>
                 <small className="text-muted">Status: {getOverallStatus(po)}</small>
                </div>
               </div>
              </td>
              <td>{po.poDate}</td>
              <td className="text-end text-primary fw-bold">{formatCurrency(po.totalAmount)}</td>
                            {/* GRN Status Column */}
              <td>
                                {getGrnStatusBadge(po.grnStatus)}
                                {po.grnId && <small className="text-muted d-block mt-1">{po.grnId}</small>}
                            </td>
                            {/* Bill Status Column */}
                            <td>
                                {getBillStatusBadge(po.billStatus)}
                                {po.billId && <small className="text-muted d-block mt-1">{po.billId}</small>}
                            </td>
              <td>
               <div className="d-flex align-items-center" style={{ gap: 8 }}>
                <button className="btn btn-sm btn-outline-info" title="View PO" onClick={() => handleViewPO(po.id)}><FiEye /></button>
                                {/* Conditional buttons for workflow progression */}
                                {po.grnStatus === "Pending" && (
                                    <button className="btn btn-sm btn-warning text-dark" title="Create GRN" onClick={() => handleCreateGRN(po.id)}><FiTruck /></button>
                                )}
                                {(po.grnStatus === "Complete" || po.grnStatus === "N/A (Service)") && po.billStatus === "N/A" && (
                                    <button className="btn btn-sm btn-success" title="Enter Bill" onClick={() => handleCreateBill(po.id)}><FiCreditCard /></button>
                                )}
               </div>
              </td>
             </tr>
            ))
           )}
          </tbody>
         </table>
        </div>
       )}
      </div>

      {/* Footer / Pagination */}
      <div className="card-footer d-flex justify-content-between align-items-center">
       <div className="text-muted">
        Showing {totalItems === 0 ? 0 : startIndex + 1} to {endIndex} of {totalItems} entries
       </div>
       <Pagination totalItems={totalItems} itemsPerPage={itemsPerPage} currentPage={currentPage} onPageChange={(p) => setCurrentPage(Math.max(1, Math.min(p, Math.max(1, Math.ceil(totalItems / itemsPerPage)))))} />
      </div>
     </div>
    </div>
   </main>
  </>
 );
}

export default PurchaseOrderTracker;